import React, { useEffect, useMemo, useState } from "react";
import getApiUrl from "../utils/api";
import "./TaskDetailsModal.css";
import DownloadAttachment from "./DownloadAttachment";
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = formatDate(dateStr);
  const timePart = formatTime(dateStr);
  return `${datePart} • ${timePart}`;
}

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const second = parts[1]?.[0] ?? "";
    const letters = `${first}${second}`.trim();
    if (letters) {
      return letters.toUpperCase();
    }
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "NA";
}

function getAvatarColor(index) {
  const colors = ["", "blue", "green", "purple", "gray"];
  return colors[index % colors.length];
}

function getStatusClass(status) {
  if (!status) return "";
  const s = status.toLowerCase();
  if (s === "completed") return "completed";
  if (s === "pending") return "pending";
  if (s === "delegated") return "delegated";
  if (s === "wip" || s === "in progress" || s === "in-progress") return "wip";
  return "";
}

function cleanDelegationRemark(remark) {
  if (!remark || typeof remark !== "string") {
    return remark || "";
  }
  return remark
    .replace(/\s*\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const TaskDetailsModal = ({ open, onClose, task, showGivenBy = false }) => {
  const [details, setDetails] = useState(task || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [visibleAttachmentsCount, setVisibleAttachmentsCount] = useState(5);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (!task) {
      setDetails(null);
      setSelectedMemberId(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setDetails(task);
    setError(null);
    setSearchQuery("");
    setVisibleAttachmentsCount(5);

    if (!task.id) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);

    fetch(getApiUrl(`api/task/${task.id}`), {
      credentials: "include",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch task details (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setDetails(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || cancelled) return;
        console.error("Failed to load task details:", err);
        setError(err.message || "Failed to load task details");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, task]);

  const rawTask = details || task;

  const allAttachments = useMemo(() => {
    const list = [];

    // Helper to process various attachment formats
    const processAtts = (atts) => {
      if (!atts) return;
      let actualAtts = atts;

      if (typeof atts === "string" && atts.trim().startsWith("[")) {
        try {
          actualAtts = JSON.parse(atts);
        } catch (e) {
          actualAtts = atts;
        }
      }

      const items = Array.isArray(actualAtts) ? actualAtts : [actualAtts];

      items.forEach((a) => {
        if (!a) return;

        if (typeof a === "string") {
          list.push({
            fileName: a.replace(/^[^-]*-[^-]*-/, ""),
            fileUrl: getApiUrl(`uploads/${a}`),
          });
        } else {
          // It's an object. Check for various keys used in the backend
          const fileName = a.fileName || a.name || a.path || "";
          const displayName = a.originalName || a.fileName || a.name || "Attachment";
          const url = a.url || a.fileUrl || (fileName ? getApiUrl(`uploads/${fileName}`) : "");

          if (url) {
            list.push({
              fileName: displayName.replace(/^[^-]*-[^-]*-/, ""),
              fileUrl: url,
            });
          }
        }
      });
    };

    // According to user clarification:
    // rawTask.actions has a 'result' key which is an array of objects.
    // Each of those objects has an 'attachments' key.

    let history = [];
    if (rawTask?.actions?.result && Array.isArray(rawTask.actions.result)) {
      history = rawTask.actions.result;
    } else if (rawTask?.actions && Array.isArray(rawTask.actions)) {
      history = rawTask.actions;
    } else if (rawTask?.action?.result && Array.isArray(rawTask.action.result)) {
      history = rawTask.action.result;
    } else if (rawTask?.action && Array.isArray(rawTask.action)) {
      history = rawTask.action;
    }

    if (Array.isArray(history)) {
      history.forEach((entry) => {
        // Look for attachments in the specified 'attachments' key or fallbacks
        const atts = entry.attachments || entry.file_attachment || entry.status_attachments;
        if (atts) {
          processAtts(atts);
        }
      });
    }

    // Remove duplicates based on fileUrl
    const uniqueList = [];
    const seenUrls = new Set();
    list.forEach(item => {
      if (!seenUrls.has(item.fileUrl)) {
        uniqueList.push(item);
        seenUrls.add(item.fileUrl);
      }
    });

    return uniqueList;
  }, [rawTask]);

  // Build members list from assignees and delegated children
  const members = useMemo(() => {
    const result = [];

    // Get delegation children for reference
    const delegationChildren = details?.delegationChildren ?? rawTask?.delegationChildren ?? [];

    // Add main assignees
    if (Array.isArray(details?.assignees) && details.assignees.length > 0) {
      details.assignees.forEach((assignee, index) => {
        const firstName = assignee.first_name || assignee.FirstName || "";
        const lastName = assignee.last_name || assignee.LastName || "";
        const name = assignee.name || [firstName, lastName].filter(Boolean).join(" ").trim() || "Unassigned";

        // Build delegated tasks for this assignee
        const delegatedTasks = delegationChildren.map((child, idx) => {
          const assigneeFirst = child?.assignee?.first_name || child?.assignee?.FirstName || "";
          const assigneeLast = child?.assignee?.last_name || child?.assignee?.LastName || "";
          const assigneeName = [assigneeFirst, assigneeLast].filter(Boolean).join(" ").trim();
          return {
            id: child?.id || `delegated-${idx}`,
            assigneeId: child?.assignee?.id || child?.assigned_to || null,
            assigneeName: assigneeName || "Unassigned",
            assigneeEmail: child?.assignee?.email || null,
            status: child?.status || "Pending",
            createdAt: child?.created_at,
            dueDate: child?.due_date,
            title: child?.title || "Delegated Task",
            remark: child?.description || child?.statusRemark || null,
          };
        });

        result.push({
          id: assignee.taskId || assignee.userId || assignee.id || `assignee-${index}`,
          name,
          email: assignee.email || null,
          status: assignee.status || rawTask?.status || "Pending",
          createdAt: assignee.createdAt || rawTask?.created_at,
          createdBy: rawTask?.given_by_name || rawTask?.GivenByFirstName ?
            `${rawTask.GivenByFirstName || ""} ${rawTask.GivenByLastName || ""}`.trim() :
            "N/A",
          dueDate: assignee.dueDate || rawTask?.due_date,
          startDate: rawTask?.start_date,
          endDate: rawTask?.due_date,
          attachments: allAttachments.length > 0 ? "Available" : "Not Available",
          timeline: assignee.timeline || [],
          delegatedTasks,
          type: "assignee",
        });
      });
    } else if (rawTask) {
      // Fallback to single assignee from task
      const firstName = rawTask.FirstName || rawTask.first_name || rawTask.assignee_name || "";
      const lastName = rawTask.LastName || rawTask.last_name || rawTask.assignee_last || "";
      const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();

      const timeline = [];
      if (rawTask.created_at) {
        timeline.push({
          id: `assigned-${rawTask.id}`,
          status: "Assigned",
          remark: null,
          createdAt: rawTask.created_at,
          createdBy: rawTask.given_by_name || "System",
        });
      }
      if (Array.isArray(rawTask.actions)) {
        rawTask.actions.forEach((action) => {
          timeline.push({
            id: action.id,
            status: action.status || action.action,
            remark: action.remark || action.remarks || null,
            createdAt: action.createdAt || action.created_at,
            createdBy: action.createdBy || "System",
          });
        });
      }

      // Build delegated tasks
      const delegatedTasks = delegationChildren.map((child, idx) => {
        const assigneeFirst = child?.assignee?.first_name || child?.assignee?.FirstName || "";
        const assigneeLast = child?.assignee?.last_name || child?.assignee?.LastName || "";
        const assigneeName = [assigneeFirst, assigneeLast].filter(Boolean).join(" ").trim();
        return {
          id: child?.id || `delegated-${idx}`,
          assigneeId: child?.assignee?.id || child?.assigned_to || null,
          assigneeName: assigneeName || "Unassigned",
          assigneeEmail: child?.assignee?.email || null,
          status: child?.status || "Pending",
          createdAt: child?.created_at,
          dueDate: child?.due_date,
          title: child?.title || "Delegated Task",
          remark: child?.description || child?.statusRemark || null,
        };
      });

      result.push({
        id: rawTask.id || "main-assignee",
        name: displayName || "Unassigned",
        email: rawTask.email || rawTask.assignee_email || null,
        status: rawTask.status || "Pending",
        createdAt: rawTask.created_at,
        createdBy: rawTask.given_by_name ||
          (rawTask.GivenByFirstName ? `${rawTask.GivenByFirstName || ""} ${rawTask.GivenByLastName || ""}`.trim() : "N/A"),
        dueDate: rawTask.due_date,
        startDate: rawTask.start_date,
        endDate: rawTask.due_date,
        attachments: allAttachments.length > 0 ? "Available" : "Not Available",
        timeline,
        delegatedTasks,
        type: "assignee",
      });
    }

    return result;
  }, [details, rawTask]);

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter((m) =>
      m.name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.status?.toLowerCase().includes(q)
    );
  }, [members, searchQuery]);

  if (!open || !task) {
    return null;
  }

  const taskTitle = rawTask?.title || "Task Details";
  const taskType = rawTask?.task_type || rawTask?.taskType || "Regular";
  const department = rawTask?.department?.name || rawTask?.department_name || "Management";
  const taskStatus = rawTask?.status || "Pending";
  const referredBy = rawTask?.given_by_name ||
    (rawTask?.GivenByFirstName ? `${rawTask.GivenByFirstName || ""} ${rawTask.GivenByLastName || ""}`.trim() : "N/A");
  const createdOn = formatDate(rawTask?.created_at);
  const startDate = rawTask?.start_date ? formatDate(rawTask.start_date) : "N/A";
  const endDate = rawTask?.due_date ? formatDate(rawTask.due_date) : "N/A";
  const hasAttachments = allAttachments.length > 0 ? `${allAttachments.length} Attachment(s)` : "No Attachments";
  const targetEndDate = rawTask?.due_date ? formatDate(rawTask.due_date) : "N/A";
  const description = rawTask?.description || "No description available";
  const project_name = rawTask?.project?.name || "N/A";

  return (
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="task-modal-header">
          <h2 className="task-modal-title">
            <span className="task-modal-title-icon"></span>
            {taskTitle}
          </h2>
          <button className="task-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* Info Grid */}
        <div className="task-modal-info-grid">
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Task Type</span>
            <span className="task-modal-info-value">{taskType}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Department</span>
            <span className="task-modal-info-value">{department}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Task Status</span>
            <span className="task-modal-info-value">{taskStatus}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Referred By</span>
            <span className="task-modal-info-value">{referredBy}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Created On</span>
            <span className="task-modal-info-value">{createdOn || "N/A"}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Start Date</span>
            <span className={`task-modal-info-value ${startDate === "N/A" ? "na" : ""}`}>{startDate}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">End Date</span>
            <span className={`task-modal-info-value ${endDate === "N/A" ? "na" : ""}`}>{endDate}</span>
          </div>
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Attachments</span>
            <span className={`task-modal-info-value ${allAttachments.length === 0 ? "na" : ""}`}>{hasAttachments}</span>
          </div>
          {
            /*<div className="task-modal-info-item">
              <span className="task-modal-info-label">Target End Date</span>
              <span className={`task-modal-info-value ${targetEndDate === "N/A" ? "na" : ""}`}>{targetEndDate}</span>
            </div>*/
          }
          <div className="task-modal-info-item">
            <span className="task-modal-info-label">Project Name</span>
            <span className={`task-modal-info-value ${project_name === "N/A" ? "na" : ""}`}>{project_name}</span>
          </div>
        </div>
        <div className="task-modal-section">
          <div className="heading">Description</div>
          {description || "No description available"}
        </div>
        {allAttachments.length > 0 && (
          <div className="task-modal-section">
            <div className="heading">Attachments ({allAttachments.length})</div>
            <div className="task-modal-attachments-grid">
              {allAttachments.slice(0, visibleAttachmentsCount).map((att, idx) => (
                <div key={idx} style={{ flex: "0 0 auto" }}>
                  <DownloadAttachment fileUrl={att.fileUrl} fileName={att.fileName} />
                </div>
              ))}
            </div>
            {visibleAttachmentsCount < allAttachments.length && (
              <button
                className="show-more-attachments-btn"
                onClick={() => setVisibleAttachmentsCount((prev) => prev + 5)}
              >
                Show More (+{Math.min(5, allAttachments.length - visibleAttachmentsCount)})
              </button>
            )}
          </div>
        )}
        {/* Members Section */}
        <div className="task-modal-members">
          <div className="task-modal-members-header">
            <h3 className="task-modal-members-title">Members Details</h3>
            <div className="task-modal-search">
              <span className="task-modal-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          {loading && (
            <div style={{ padding: "20px", textAlign: "center", color: "#6b7280" }}>
              Loading members...
            </div>
          )}

          {error && (
            <div style={{ padding: "20px", textAlign: "center", color: "#dc2626" }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="task-modal-members-list">
                {filteredMembers.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#6b7280" }}>
                    No members found
                  </div>
                ) : (
                  filteredMembers.map((member, index) => {
                    const isExpanded = selectedMemberId === String(member.id);
                    return (
                      <div key={member.id} className="task-modal-member-wrapper">
                        <div
                          className={`task-modal-member-row ${isExpanded ? "selected" : ""}`}
                          onClick={() => setSelectedMemberId(isExpanded ? null : String(member.id))}
                        >
                          {/* Member Info */}
                          <div className="task-modal-member-info">
                            <div className={`task-modal-member-avatar ${getAvatarColor(index)}`}>
                              {getInitials(member.name, member.email)}
                            </div>
                            <div className="task-modal-member-details">
                              <span className="task-modal-member-name">{member.name}</span>
                              <span className={`task-modal-member-status-label ${getStatusClass(member.status)}`}>
                                Task Status: {member.status}
                              </span>
                            </div>
                          </div>

                          {/* Date */}
                          <div className="task-modal-member-date">
                            <span className="task-modal-member-date-value">{formatDate(member.createdAt)}</span>
                            <span className="task-modal-member-date-time">{formatTime(member.createdAt)}</span>
                          </div>

                          {/* Created By */}
                          <div className={`task-modal-member-created-by ${member.createdBy === "N/A" ? "na" : ""}`}>
                            {member.createdBy === "N/A" ? "No Data found" : member.createdBy}
                          </div>

                          {/* Status Badge */}
                          <div>
                            <span className={`task-modal-status-badge ${getStatusClass(member.status)}`}>
                              {member.status}
                            </span>
                          </div>

                          {/* Attachments 
                          <div className={`task-modal-member-attachments ${member.attachments === "Not Available" ? "na" : ""}`}>
                            {member.attachments === "Not Available" ? "Attachments\nNot Available" : "Attachments\nAvailable"}
                          </div>*/}

                          {/* Actions */}
                          <div className="task-modal-member-actions">
                            {/* <button className="task-modal-action-btn" title="View Details">
                              👁️
                            </button>*/}
                            <button className="task-modal-action-btn" title="More Options">
                              ⋮
                            </button>
                          </div>
                        </div>

                        {/* Expanded Timeline - Inline below member row */}
                        {isExpanded && (
                          <div className="task-modal-member-expanded" onClick={(e) => e.stopPropagation()}>
                            <div className="task-modal-member-expanded-header">
                              <div className="task-modal-member-expanded-left">
                                <div className="task-modal-member-expanded-avatar">
                                  {getInitials(member.name, member.email)}
                                </div>
                                <div className="task-modal-member-expanded-info">
                                  <div className="task-modal-member-expanded-name">{member.name}</div>
                                  <div className="task-modal-member-expanded-email">
                                    {member.email || "No email available"}
                                  </div>
                                </div>
                              </div>
                              <div className="task-modal-member-expanded-right">
                                <span className={`task-modal-status-badge ${getStatusClass(member.status)}`}>
                                  {member.status}
                                </span>
                                <button
                                  className="task-modal-timeline-close"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedMemberId(null);
                                  }}
                                  aria-label="Close timeline"
                                >
                                  ×
                                </button>
                              </div>
                            </div>

                            {member.remark && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                                  Remark
                                </div>
                                <div style={{ fontSize: "0.9rem", color: "#4b5563", background: "#f9fafb", padding: 12, borderRadius: 8 }}>
                                  {member.remark}
                                </div>
                              </div>
                            )}

                            {/* Delegated Tasks Section */}
                            {member.delegatedTasks && member.delegatedTasks.length > 0 && (
                              <div className="task-modal-delegated-section">
                                <div className="task-modal-delegated-title">
                                  <span className="task-modal-delegated-icon">↪</span>
                                  Delegated Tasks ({member.delegatedTasks.length})
                                </div>
                                <div className="task-modal-delegated-list">
                                  {member.delegatedTasks.map((delegated) => (
                                    <div key={delegated.id} className="task-modal-delegated-item">
                                      <div className="task-modal-delegated-avatar">
                                        {getInitials(delegated.assigneeName, delegated.assigneeEmail)}
                                      </div>
                                      <div className="task-modal-delegated-info">
                                        <div className="task-modal-delegated-name">{delegated.assigneeName}</div>
                                        {delegated.assigneeEmail && (
                                          <div className="task-modal-delegated-email">{delegated.assigneeEmail}</div>
                                        )}
                                        {delegated.title && (
                                          <div className="task-modal-delegated-task-name">
                                            Task: {delegated.title}
                                          </div>
                                        )}
                                        <div className="task-modal-delegated-dates">
                                          <span className="task-modal-delegated-date-item">
                                            📅 Created: {formatDateTime(delegated.createdAt)}
                                          </span>
                                          {delegated.dueDate && (
                                            <span className="task-modal-delegated-date-item">
                                              ⏰ Due: {formatDate(delegated.dueDate)}
                                            </span>
                                          )}
                                        </div>
                                        {cleanDelegationRemark(delegated.remark) && (
                                          <div className="task-modal-delegated-remark">
                                            {cleanDelegationRemark(delegated.remark)}
                                          </div>
                                        )}
                                      </div>
                                      <span className={`task-modal-status-badge ${getStatusClass(delegated.status)}`}>
                                        {delegated.status}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {member.timeline && member.timeline.length > 0 && (
                              <div className="task-modal-timeline">
                                <div className="task-modal-timeline-title">Activity Timeline</div>
                                <ul className="task-modal-timeline-list">
                                  {member.timeline
                                    .filter((event) => {
                                      // Hide "Delegation Released" entries
                                      const status = event.status?.toLowerCase() || '';
                                      return !status.includes('released');
                                    })
                                    .map((event) => {
                                      const isDelegation = event.status?.toLowerCase().includes('delegat');
                                      const isCompleted = event.status?.toLowerCase() === 'completed';
                                      const isAssigned = event.status?.toLowerCase() === 'assigned';
                                      const isWIP = event.status?.toLowerCase() === 'wip' || event.status?.toLowerCase().includes('progress');

                                      // Resolve delegation target info
                                      let delegatedTarget = event.targetUser || null;
                                      const normalizedTargetId = event.targetUser?.id
                                        ? String(event.targetUser.id)
                                        : null;
                                      if (isDelegation && !delegatedTarget) {
                                        let parsedName = null;
                                        if (event.remark) {
                                          const match = event.remark.match(/delegated to\s+([^—]+)/i);
                                          if (match) {
                                            parsedName = match[1].trim();
                                          }
                                        }

                                        if (parsedName) {
                                          delegatedTarget = {
                                            name: parsedName,
                                            email: null,
                                          };
                                        }

                                        if (member.delegatedTasks && member.delegatedTasks.length > 0) {
                                          const matchedDelegate = member.delegatedTasks.find((d) => {
                                            if (normalizedTargetId && d.assigneeId) {
                                              return String(d.assigneeId) === normalizedTargetId;
                                            }
                                            if (event.targetUser?.email && d.assigneeEmail) {
                                              return (
                                                d.assigneeEmail.toLowerCase() ===
                                                event.targetUser.email.toLowerCase()
                                              );
                                            }
                                            if (parsedName && d.assigneeName) {
                                              return d.assigneeName.toLowerCase() === parsedName.toLowerCase();
                                            }
                                            return false;
                                          });

                                          if (matchedDelegate) {
                                            delegatedTarget = {
                                              name:
                                                matchedDelegate.assigneeName ||
                                                delegatedTarget?.name ||
                                                "Unassigned",
                                              email:
                                                matchedDelegate.assigneeEmail ||
                                                delegatedTarget?.email ||
                                                null,
                                            };
                                          }
                                        }
                                      }

                                      if (
                                        isDelegation &&
                                        delegatedTarget &&
                                        member.delegatedTasks &&
                                        member.delegatedTasks.length > 0
                                      ) {
                                        const matchedDelegate = member.delegatedTasks.find((d) => {
                                          if (normalizedTargetId && d.assigneeId) {
                                            return String(d.assigneeId) === normalizedTargetId;
                                          }
                                          if (delegatedTarget.email && d.assigneeEmail) {
                                            return (
                                              d.assigneeEmail.toLowerCase() ===
                                              delegatedTarget.email.toLowerCase()
                                            );
                                          }
                                          return false;
                                        });

                                        if (matchedDelegate) {
                                          delegatedTarget = {
                                            name:
                                              matchedDelegate.assigneeName ||
                                              delegatedTarget.name,
                                            email:
                                              matchedDelegate.assigneeEmail ||
                                              delegatedTarget.email,
                                          };
                                        }
                                      }

                                      return (
                                        <li
                                          key={event.id || event.createdAt}
                                          className={`task-modal-timeline-item ${getStatusClass(event.status)} ${isDelegation ? 'delegation' : ''}`}
                                        >
                                          <span className="task-modal-timeline-marker"></span>
                                          <div className="task-modal-timeline-content">
                                            <div className="task-modal-timeline-header">
                                              <div className="task-modal-timeline-status">{event.status}</div>
                                              {event.createdBy && (
                                                <div className="task-modal-timeline-actor">
                                                  <span className="task-modal-timeline-actor-label">by</span>
                                                  <span className="task-modal-timeline-actor-name">{event.createdBy}</span>
                                                </div>
                                              )}
                                            </div>

                                            {event.remark && !isDelegation && (
                                              <div className="task-modal-timeline-remark">
                                                {event.remark}
                                              </div>
                                            )}

                                            {/* Show target user info for non-delegation events if provided */}
                                            {!isDelegation && event.targetUser && (
                                              <div className="task-modal-timeline-target">
                                                <div className="task-modal-timeline-target-avatar">
                                                  {getInitials(event.targetUser.name, event.targetUser.email)}
                                                </div>
                                                <div className="task-modal-timeline-target-info">
                                                  <div className="task-modal-timeline-target-name">{event.targetUser.name}</div>
                                                  {event.targetUser.email && (
                                                    <div className="task-modal-timeline-target-email">{event.targetUser.email}</div>
                                                  )}
                                                </div>
                                              </div>
                                            )}

                                            <div className="task-modal-timeline-meta">
                                              <span className="task-modal-timeline-date">
                                                {formatDateTime(event.createdAt)}
                                              </span>
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    })}
                                </ul>
                              </div>
                            )}

                            {(!member.timeline || member.timeline.length === 0) && (!member.delegatedTasks || member.delegatedTasks.length === 0) && (
                              <div style={{ color: "#9ca3af", fontSize: "0.85rem", textAlign: "center", padding: "20px 0" }}>
                                No activity recorded for this member
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div>

              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;
